/**
 * Utility functions for TitCock.tv
 */

// DOM Helpers
export const $ = (id) => document.getElementById(id);
export const $$ = (selector) => document.querySelector(selector);
export const $$$ = (selector) => document.querySelectorAll(selector);

/**
 * Create a DOM element with optional class and text content
 * @param {string} tag - HTML tag name
 * @param {string} [className] - CSS class(es)
 * @param {string} [text] - Text content
 * @returns {HTMLElement}
 */
export const createElement = (tag, className, text) => {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text != null) el.textContent = text;
  return el;
};

/**
 * Clamp a value between min and max
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

/**
 * Format large numbers (1000 → 1K, 1000000 → 1M)
 * @param {number} n
 * @returns {string}
 */
export const formatNumber = (n) => {
  n = Number(n) || 0;
  if (n >= 1e6) {
    return (n / 1e6).toFixed(n % 1e6 ? 1 : 0).replace(/\.0$/, "") + "M";
  }
  if (n >= 1e3) {
    return `${Math.floor(n / 1e3)}K`;
  }
  return String(n);
};

/**
 * Format score with sign (+1234 or -1234)
 * @param {number} score
 * @returns {string}
 */
export const formatScore = (score) => {
  const n = Number(score);
  return n >= 0 ? `+${n}` : `${n}`;
};

/**
 * Remove trailing slashes from URL
 * @param {string} url
 * @returns {string}
 */
export const normalizeUrl = (url) => url.replace(/\/+$/, "");

/**
 * Get viewport height (accounting for mobile browsers)
 * @returns {number}
 */
export const getViewportHeight = () =>
  window.visualViewport?.height || window.innerHeight;

/**
 * Create CSS transform string for vertical translation
 * @param {number} dy - Y offset in pixels
 * @returns {string}
 */
export const translateY = (dy) => `translate3d(0,${dy}px,0)`;

/**
 * Get current vertical translation from transform matrix
 * @param {HTMLElement} el
 * @returns {number}
 */
export const getTranslateY = (el) => {
  if (!el) return 0;
  const style = window.getComputedStyle(el);
  const matrix = new DOMMatrix(style.transform);
  return matrix.m42;
};

/**
 * Get current horizontal translation from transform matrix
 * @param {HTMLElement} el
 * @returns {number}
 */
export const getTranslateX = (el) => {
  if (!el) return 0;
  const style = window.getComputedStyle(el);
  const matrix = new DOMMatrix(style.transform);
  return matrix.m41;
};

/**
 * Debounce a function
 * @param {Function} fn
 * @param {number} delay
 * @returns {Function}
 */
export const debounce = (fn, delay) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
};

/**
 * Throttle a function
 * @param {Function} fn
 * @param {number} limit
 * @returns {Function}
 */
export const throttle = (fn, limit) => {
  let inThrottle;
  return (...args) => {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Wait for a specified duration
 * @param {number} ms
 * @returns {Promise<void>}
 */
export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Check if an element is in the viewport
 * @param {HTMLElement} el
 * @returns {boolean}
 */
export const isInViewport = (el) => {
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};
